import { forwardRef, useEffect, useRef, useState } from 'react';
import { NavLink, Text } from '@mantine/core';
import { useEventListener } from '@mantine/hooks';
import clsx from 'clsx';

import { GoogleFontIcon } from '../../icons/GoogleFontIcon';
import { TextWithTooltip } from '../../tooltip/TextWithTooltip';

import styles from './navigationItem.module.scss';

export type NavigationItemProps = {
  active?: boolean;
  collapsed: boolean;
  collapsedTooltipWidth?: string;
  iconName: string;
  isDetailsPageSection?: boolean;
  label: string;
  sublabel?: string;
  tooltip?: string;
  onClick?: () => void;
  onClose?: () => void;
  isPending?: boolean;
};

const NavigationItemContent = forwardRef<
  HTMLParagraphElement,
  Omit<NavigationItemProps, 'collapsedTooltipWidth'> & {
    label: string;
    sublabel?: string;
    iconName: string;
  }
>(
  (
    {
      active = false,
      iconName,
      isDetailsPageSection,
      label,
      sublabel,
      collapsed,
      onClick,
      onClose,
      isPending = false,
    },
    ref
  ) => {
    const { ref: navLinkRef } = useScrollIntoView({ isPending, active });

    return (
      <NavLink
        ref={navLinkRef}
        component="button"
        label={
          !collapsed && (
            <Text ref={ref} inherit lineClamp={2}>
              {label}
              {sublabel != null && (
                <span className={styles['sub-label']}>{sublabel}</span>
              )}
            </Text>
          )
        }
        leftSection={<GoogleFontIcon solid small name={iconName} />}
        rightSection={
          !collapsed && onClose ? (
            <GoogleFontIcon
              className={styles['close-icon']}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              solid
              small
              name="close"
            />
          ) : undefined
        }
        classNames={{
          root: clsx(
            styles['root'],
            collapsed && styles['root--collapsed'],
            active && styles['root--active'],
            !active &&
              isDetailsPageSection &&
              styles['root--details-page-section'],
            onClick != null && styles['root--clickable']
          ),
          body: styles['body'],
          section: styles['icon'],
          label: styles['label'],
        }}
        unstyled
        onClick={onClick}
      />
    );
  }
);

export const NavigationItem = ({
  active,
  collapsed,
  collapsedTooltipWidth,
  iconName,
  isDetailsPageSection,
  label,
  sublabel,
  tooltip,
  onClick,
  onClose,
  isPending,
}: NavigationItemProps) => {
  const [disabled, setDisabled] = useState(true);
  const ref = useEventListener('mouseover', () => {
    if (ref.current) {
      setDisabled(ref.current.scrollHeight <= ref.current.clientHeight + 1);
    }
  });

  return (
    <TextWithTooltip
      tooltipProps={{
        position: 'right',
        label: tooltip ?? label,
        width: collapsedTooltipWidth,
        disabled: !collapsed && disabled,
      }}
      textProps={{ span: true }}
    >
      <NavigationItemContent
        ref={ref}
        active={active}
        collapsed={collapsed}
        iconName={iconName}
        isDetailsPageSection={isDetailsPageSection}
        key={label}
        label={label}
        onClick={onClick}
        onClose={onClose}
        sublabel={sublabel}
        isPending={isPending}
      />
    </TextWithTooltip>
  );
};

function useScrollIntoView({
  isPending,
  active,
}: {
  isPending: boolean;
  active: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const scrollParent = getScrollParent(ref.current);
    if (active && !isPending && ref.current && scrollParent) {
      const scrollParentDOMRect = scrollParent.getBoundingClientRect();
      const navLinkDOMRect = ref.current.getBoundingClientRect();
      const cutOff =
        navLinkDOMRect.top < scrollParentDOMRect.top ||
        navLinkDOMRect.bottom > scrollParentDOMRect.bottom;

      if (cutOff) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [isPending, active]);

  return { ref };
}

function getScrollParent(node: HTMLElement | null): HTMLElement | null {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return node.parentNode instanceof HTMLElement
      ? getScrollParent(node.parentNode)
      : null;
  }
}
